import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  useTheme,
  Link as MuiLink,
} from '@mui/material';
import { Link } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  const theme = useTheme();
  const lastUpdated = 'February 17, 2026';

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
          pt: { xs: 4, md: 6 },
          pb: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Privacy Policy
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Last updated: {lastUpdated}
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, border: `1px solid ${theme.palette.divider}` }}>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            This Privacy Policy explains how Jakatech LLC, a Texas limited liability company doing business as
            Tier One Content ("Jakatech," "we," "us," or "our"), collects, uses, and protects personal data when
            you use the Tier One Content platform (the "Service").
          </Typography>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            By using the Service, you agree to the practices described in this Policy.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            1. Who We Are
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, fontWeight: 600 }}>
            Legal Entity:
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Jakatech LLC<br />
            d/b/a Tier One Content<br />
            10228 E Northwest Hwy #1001<br />
            Dallas, TX 75238<br />
            Email: privacy@tieronecontent.com
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Jakatech acts as:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Data Controller for account registration, billing, authentication, and platform administration data.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Data Processor (or Service Provider) for personal data contained within content uploaded by Organizations and Users.
              </Typography>
            </li>
          </ul>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            2. Information We Collect
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We collect the following categories of information:
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            A. Account Information
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Name
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Email address
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Organization affiliation
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Team memberships
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Account credentials (stored securely in hashed form)
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            B. Age Verification
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Date of birth (used only to verify eligibility)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Stored privately and not displayed or shared
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            C. Usage Data
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Login history
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                User activity within the Service
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                IP address
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Device and browser information
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Error logs
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            D. Content Data
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Files and media you upload
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Metadata associated with uploaded content
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Tags, summaries, indexing data generated by the Service
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            E. Communications
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Emails
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                In-app messages
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                SMS notifications (if enabled)
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            F. Payment Information
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Payment data is processed securely by third-party payment providers (e.g., Stripe). We do not store
            full credit card numbers.
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            G. Cookie & Preference Data
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Cookie consent choices
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Policy version acknowledgement
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Preference settings
              </Typography>
            </li>
          </ul>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            3. User-Uploaded Content & Data Protection Roles
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            When Users or Organizations upload content:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                The uploading Organization or User acts as the Data Controller (under GDPR) or Business (under CCPA).
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Jakatech acts as a Data Processor (under GDPR) or Service Provider (under CCPA), processing such data solely to provide the Service.
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You are responsible for ensuring that:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                You have lawful basis to upload and share personal data.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                You have obtained required consents where necessary.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Your uploads comply with applicable privacy and data protection laws.
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Jakatech does not routinely inspect uploaded content but reserves the right to remove content that
            violates our Terms of Service or applicable law.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We do not "sell" or "share" personal data as defined under the California Consumer Privacy Act.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Our Data Processing Agreement (DPA), where applicable, governs processor obligations.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            4. How We Use Information
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We process personal data for the following purposes:
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            To Provide the Service
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Account creation and authentication
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Content hosting and sharing
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Organization and Team administration
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            To Improve and Enhance the Service
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Generate tags and summaries
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Improve search functionality
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Personalize content recommendations
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Analyze performance and usage trends
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            To Maintain Security
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Detect fraud
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Prevent abuse
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Monitor suspicious activity
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Enforce Terms of Service
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            To Communicate With You
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Service updates
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Security notifications
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Billing notices
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            To Comply With Legal Obligations
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Tax compliance
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Law enforcement requests
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Regulatory compliance
              </Typography>
            </li>
          </ul>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            5. Legal Bases for Processing (GDPR)
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Where GDPR applies, we rely on:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Contractual Necessity</strong> – To provide the Service
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Legitimate Interests</strong> – To improve security, analytics, and platform functionality
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Legal Obligation</strong> – To comply with applicable laws
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Consent</strong> – For marketing communications or optional cookies
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You may withdraw consent at any time where consent is the basis for processing.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            6. Sharing & Disclosure
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We may share personal data:
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            With Service Providers
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Trusted vendors who assist in:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Hosting and infrastructure
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Database services
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Payment processing
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Email and SMS delivery
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Analytics tools
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            These providers are contractually obligated to protect data.
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            For Legal Reasons
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If required by law, subpoena, or regulatory request.
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            Corporate Transactions
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            In connection with mergers, acquisitions, or asset sales.
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            With Your Consent
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Where you explicitly authorize sharing.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, fontWeight: 600 }}>
            We do not sell personal data.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            7. Your Rights
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Depending on your location, you may have the right to:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Access your personal data
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Correct inaccurate data
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Request deletion
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Restrict processing
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Object to processing
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Request data portability
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Withdraw consent
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Lodge a complaint with a supervisory authority
              </Typography>
            </li>
          </ul>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            Privacy Center
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You may exercise many rights through the Privacy Center at:{' '}
            <MuiLink component={Link} to="/privacy-center">
              /privacy-center
            </MuiLink>
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You can:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Download your personal data (JSON format)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Manage cookie preferences
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Delete your account
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            For additional requests, contact: privacy@tieronecontent.com
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We respond within legally required timeframes.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            8. Data Retention
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We retain personal data only as long as necessary:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Account data – Retained while active; deleted within 30 days of account deletion
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Usage logs – Retained for 90 days
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Billing records – Retained for 7 years (tax compliance)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Marketing consent – Until withdrawn or 24 months of inactivity
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Cookie preferences – Retained 12 months or until changed
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Backup systems may retain encrypted data for limited additional periods.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            9. Cookies & Tracking Technologies
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We use cookies and similar technologies for:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Essential functionality
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Preferences
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Analytics
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Marketing (if enabled)
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            On first visit, you will see a cookie consent banner allowing you to:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Accept essential cookies
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Enable or disable analytics
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Enable or disable marketing cookies
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You may change preferences at any time via Profile settings or the Privacy Center.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            For details, see our{' '}
            <MuiLink component={Link} to="/legal/cookies">
              Cookie Policy
            </MuiLink>
            .
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            10. International Data Transfers
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We operate primarily in the United States.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If personal data is transferred outside your jurisdiction, including outside the European Economic
            Area (EEA), we rely on appropriate safeguards such as:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Standard Contractual Clauses (SCCs)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                EU–US Data Privacy Framework (where applicable)
              </Typography>
            </li>
          </ul>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            11. Security
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We implement commercially reasonable technical and organizational measures including:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Encryption in transit (TLS)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Encryption at rest
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Access controls
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Audit logging
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Regular security updates
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            No system is completely secure, but we take data protection seriously.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            12. Children's Privacy
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            The Service is not intended for children under 16, or the minimum age required under applicable law.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We do not knowingly collect personal data from children below this age. If we become aware of such
            data, we will delete it.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            13. Changes to This Policy
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We may update this Privacy Policy from time to time.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If changes are material, we will provide notice via:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Email
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Platform notification
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Updated "Last Updated" date
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Continued use of the Service after changes constitutes acceptance.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            14. Contact Us
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            Jakatech LLC<br />
            d/b/a Tier One Content<br />
            10228 E Northwest Hwy #1001<br />
            Dallas, TX 75238<br />
            Email: privacy@tieronecontent.com
          </Typography>

          <Divider sx={{ my: 5 }} />

          <Typography variant="body2" color="text.secondary">
            By using Tier One Content, you acknowledge that you have read and understand this Privacy Policy.
            Last updated: {lastUpdated}.
          </Typography>
        </Paper>
      </Container>
    </>
  );
};

export default PrivacyPolicy;
